// Phase F5 — original 6 seat-border ids + 8 new entries (obsidian,
// gilded-rose, wrought-iron, crystal-shard, solstice-flame, runic-engraved,
// tide-foam, void-ring). Animations scale with rarity per the doc's
// rarity policy; selectors live in the seat-cosmetic CSS section in
// `web/src/styles/global.css`.
export type SeatBorderId =
  | 'none' | 'ember' | 'frost' | 'vine' | 'storm' | 'champion'
  | 'obsidian' | 'gilded-rose' | 'wrought-iron' | 'crystal-shard'
  | 'solstice-flame' | 'runic-engraved' | 'tide-foam' | 'void-ring';

export type SeatBackgroundId =
  | 'plain' | 'tavern' | 'moon' | 'spice' | 'lantern' | 'arcane'
  | 'parchment' | 'velvet-curtain' | 'forge-glow' | 'library'
  | 'garden' | 'cosmos' | 'tide-pool' | 'eclipse';

export type SeatParticleId =
  | 'none' | 'embers' | 'snow' | 'sparks' | 'cards' | 'fireflies'
  | 'petals' | 'bubbles' | 'lightning' | 'confetti' | 'runes'
  | 'phoenix-feathers' | 'shadows' | 'coins';

/**
 * Walkable-hall movement bounds (world units, XZ plane). ONE definition
 * shared by every enforcement point: ws-router's Lambda relay clamp, the
 * gameroom RT presence relay clamp, and the client's local walker. The
 * hall geometry in RoomBackdrop3DScene/tavernHall is built to these.
 */
export const MOVE_BOUNDS = { minX: -8, maxX: 8, minZ: -6, maxZ: 3.5 } as const;

export interface SeatCosmetics {
  border: SeatBorderId;
  background: SeatBackgroundId;
  particle: SeatParticleId;
  /** Optional hue (0–359). When set, overrides the preset background with
   *  a hue-driven gradient. Null/undefined = use the preset background. */
  bgHue?: number | null;
}

/** Catalog of seat-background hues. Each is a hue 0–359 + a label. The CSS
 *  uses these as data attributes; the player's seatBgHues inventory gates
 *  which they can equip. */
export const SEAT_BG_HUES: Array<{ id: number; label: string }> = [
  { id: 0,   label: 'Crimson' },
  { id: 15,  label: 'Burnt Sienna' },
  { id: 25,  label: 'Ember' },
  { id: 45,  label: 'Mustard' },
  { id: 50,  label: 'Honey' },
  { id: 75,  label: 'Olive' },
  { id: 90,  label: 'Moss' },
  { id: 120, label: 'Pine' },
  { id: 140, label: 'Forest' },
  { id: 180, label: 'Tide' },
  { id: 200, label: 'Storm' },
  { id: 210, label: 'Azure' },
  { id: 250, label: 'Indigo' },
  { id: 265, label: 'Periwinkle' },
  { id: 290, label: 'Velvet' },
  { id: 330, label: 'Rose' },
];

const bgHueIds = new Set(SEAT_BG_HUES.map((h) => h.id));

export interface PresenceCatalogItem<T extends string> {
  id: T;
  label: string;
  description: string;
}

export const DEFAULT_SEAT_COSMETICS: SeatCosmetics = {
  border: 'none',
  background: 'plain',
  particle: 'none',
  bgHue: null,
};

export const SEAT_BORDERS: Array<PresenceCatalogItem<SeatBorderId>> = [
  { id: 'none', label: 'Plain frame', description: 'The default lobby tile frame.' },
  { id: 'ember', label: 'Ember frame', description: 'A warm fire-lit border for your lobby seat.' },
  { id: 'frost', label: 'Frost frame', description: 'An icy rim with a cold blue glow.' },
  { id: 'vine', label: 'Vine frame', description: 'A green grown-over border for steady players.' },
  { id: 'storm', label: 'Storm frame', description: 'A charged purple frame for high profile-point players.' },
  { id: 'champion', label: 'Champion frame', description: 'A gold winner frame for proven set champions.' },
  // Phase F5 additions
  { id: 'obsidian',      label: 'Obsidian frame',      description: 'Black volcanic stone with subtle red veins.' },
  { id: 'gilded-rose',   label: 'Gilded Rose frame',   description: 'Gold filigree with rose-gold accents.' },
  { id: 'wrought-iron',  label: 'Wrought Iron frame',  description: 'Twisted iron bar pattern.' },
  { id: 'crystal-shard', label: 'Crystal Shard frame', description: 'Refracting crystal facets with a subtle hue-shift.' },
  { id: 'solstice-flame',label: 'Solstice Flame frame',description: 'Animated flame tongues — Crimson Solstice premium.' },
  { id: 'runic-engraved',label: 'Runic Engraved frame',description: 'Carved arcane runes that pulse softly.' },
  { id: 'tide-foam',     label: 'Tide Foam frame',     description: 'Wave / foam pattern with a slow drift.' },
  { id: 'void-ring',     label: 'Void Ring frame',     description: 'Black ring flecked with starlight, radial pulse.' },
];

export const SEAT_BACKGROUNDS: Array<PresenceCatalogItem<SeatBackgroundId>> = [
  { id: 'plain', label: 'Plain table', description: 'The default lobby tile background.' },
  { id: 'tavern', label: 'Tavern glow', description: 'A soft candlelit backdrop behind your avatar.' },
  { id: 'moon', label: 'Moonlit booth', description: 'A cool moonbeam backdrop earned through streaks.' },
  { id: 'spice', label: 'Spice rush', description: 'A hot red-orange backdrop tied to Spice Rush play.' },
  { id: 'lantern', label: 'Lantern light', description: 'A bright table glow for repeat winners.' },
  { id: 'arcane', label: 'Arcane circle', description: 'A magic circle backdrop for award collectors.' },
  // Phase F5 additions
  { id: 'parchment',      label: 'Parchment',      description: 'Old paper texture, warm and quiet.' },
  { id: 'velvet-curtain', label: 'Velvet Curtain', description: 'Deep purple curtain folds.' },
  { id: 'forge-glow',     label: 'Forge Glow',     description: 'Animated forge fire backdrop — Crimson Solstice premium.' },
  { id: 'library',        label: 'Library',        description: 'Bookshelves with drifting dust motes.' },
  { id: 'garden',         label: 'Garden',         description: 'Leafy garden with a subtle leaf drift.' },
  { id: 'cosmos',         label: 'Cosmos',         description: 'Animated star field with a slow nebula color shift.' },
  { id: 'tide-pool',      label: 'Tide Pool',      description: 'Water surface with subtle ripples.' },
  { id: 'eclipse',        label: 'Eclipse',        description: 'Mythic moon-eclipse-fire with a corona.' },
];

export const SEAT_PARTICLES: Array<PresenceCatalogItem<SeatParticleId>> = [
  { id: 'none', label: 'No particles', description: 'No ambient effects on your lobby tile.' },
  { id: 'embers', label: 'Embers', description: 'Small fire sparks floating around your seat.' },
  { id: 'snow', label: 'Snow drift', description: 'Slow icy motes around your lobby tile.' },
  { id: 'sparks', label: 'Gold sparks', description: 'Quick bright sparks for frequent winners.' },
  { id: 'cards', label: 'Card shimmer', description: 'Tiny card-like glints for set winners.' },
  { id: 'fireflies', label: 'Fireflies', description: 'Soft glowing dots for streak players.' },
  // Phase F5 additions
  { id: 'petals',           label: 'Petals',           description: 'Floating rose / sakura petals.' },
  { id: 'bubbles',          label: 'Bubbles',          description: 'Soft floating bubbles.' },
  { id: 'lightning',        label: 'Lightning',        description: 'Occasional bright zaps with afterglow.' },
  { id: 'confetti',         label: 'Confetti',         description: 'Colorful confetti rain that resets and rains again.' },
  { id: 'runes',            label: 'Runes',            description: 'Slowly orbiting runic glyphs — Crimson Solstice premium.' },
  { id: 'phoenix-feathers', label: 'Phoenix Feathers', description: 'Drifting glowing red / gold feathers.' },
  { id: 'shadows',          label: 'Shadows',          description: 'Subtle dark wisps that swirl.' },
  { id: 'coins',            label: 'Coins',            description: 'Slow gold coin drift.' },
];

const borderIds = new Set(SEAT_BORDERS.map((x) => x.id));
const backgroundIds = new Set(SEAT_BACKGROUNDS.map((x) => x.id));
const particleIds = new Set(SEAT_PARTICLES.map((x) => x.id));

export function normalizeSeatCosmetics(value: unknown): SeatCosmetics {
  const raw = (value && typeof value === 'object') ? value as Partial<SeatCosmetics> : {};
  const rawHue = (raw as { bgHue?: unknown }).bgHue;
  const bgHue = typeof rawHue === 'number' && bgHueIds.has(rawHue) ? rawHue : null;
  return {
    border: borderIds.has(raw.border as SeatBorderId) ? raw.border as SeatBorderId : DEFAULT_SEAT_COSMETICS.border,
    background: backgroundIds.has(raw.background as SeatBackgroundId) ? raw.background as SeatBackgroundId : DEFAULT_SEAT_COSMETICS.background,
    particle: particleIds.has(raw.particle as SeatParticleId) ? raw.particle as SeatParticleId : DEFAULT_SEAT_COSMETICS.particle,
    bgHue,
  };
}

export function seatCosmeticsAllowed(value: SeatCosmetics, inventory: {
  seatBorders: SeatBorderId[];
  seatBackgrounds: SeatBackgroundId[];
  seatParticles: SeatParticleId[];
  seatBgHues?: number[];
}): boolean {
  if (value.bgHue !== null && value.bgHue !== undefined) {
    if (!(inventory.seatBgHues ?? []).includes(value.bgHue)) return false;
  }
  return inventory.seatBorders.includes(value.border)
    && inventory.seatBackgrounds.includes(value.background)
    && inventory.seatParticles.includes(value.particle);
}

/** R2#86 — Strict validator for write-path use. `normalizeSeatCosmetics` is
 *  intentionally permissive (it silently coerces unknown bgHues to null so
 *  display contexts don't crash on stale rows). The write path needs the
 *  opposite: a player submitting an unknown or unowned bgHue should get a
 *  400 `locked-bg-hue` error rather than silently having the value dropped.
 *  Returns the failure code to surface, or null if the input passes. */
export function validateSeatCosmeticsRequest(
  rawValue: unknown,
  inventory: {
    seatBorders: SeatBorderId[];
    seatBackgrounds: SeatBackgroundId[];
    seatParticles: SeatParticleId[];
    seatBgHues?: number[];
  },
): null | 'locked-bg-hue' | 'locked-seat-cosmetic' {
  const raw = (rawValue && typeof rawValue === 'object') ? (rawValue as Partial<SeatCosmetics>) : {};
  const rawHue = (raw as { bgHue?: unknown }).bgHue;
  // bgHue is allowed to be null / undefined (no preset hue chosen). When
  // present, it MUST be both a known hue id AND owned by the player.
  if (rawHue !== null && rawHue !== undefined) {
    if (typeof rawHue !== 'number' || !bgHueIds.has(rawHue)) return 'locked-bg-hue';
    if (!(inventory.seatBgHues ?? []).includes(rawHue)) return 'locked-bg-hue';
  }
  const normalized = normalizeSeatCosmetics(rawValue);
  if (!seatCosmeticsAllowed(normalized, inventory)) return 'locked-seat-cosmetic';
  return null;
}
